import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import WaitingProspectsWidget from "@/components/WaitingProspectsWidget";
import { getSEO } from "@/lib/seo-config";
import {
  ShieldCheck, Lock, Camera, Scale, ArrowRight, CheckCircle2, XCircle,
  BadgeCheck, Award, FileText, Banknote, HardHat, Sparkles, Wallet,
  MapPin, Zap, Star, Users, TrendingUp, Clock, Building2,
  MessageSquare, ClipboardList, CalendarDays, Link2, Gift, BellRing, Receipt,
} from "lucide-react";

const Overline = ({ children, color = "#FF4D00" }) => (
  <div className="mono text-[11px] uppercase tracking-widest font-semibold" style={{ color }}>{children}</div>
);

export default function JoinArtisan() {
  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid="join-artisan-page">
      <SEO cfg={getSEO("/pro/rejoindre")} />
      <Navbar />

      {/* WELCOME OFFER STRIPE */}
      <div className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]" data-testid="welcome-offer-banner">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center md:text-left">
          <div className="flex items-center gap-3">
            <div className="text-2xl">🎁</div>
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-white/80">// Offre de bienvenue</div>
              <div className="text-sm md:text-base font-semibold">
                <strong>1 mois de Pack Pro OFFERT</strong> à l'inscription · Sans carte bancaire · Sans engagement
              </div>
            </div>
          </div>
          <Link
            to="/register?role=artisan&trial=1"
            className="bg-white text-[#FF4D00] font-semibold px-4 py-2 mono text-[11px] uppercase tracking-widest hover:bg-[#0A0A0B] hover:text-[#FF4D00] transition-colors flex items-center gap-1.5 shrink-0"
            data-testid="welcome-offer-cta"
          >
            Réclamer mon essai <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0B] bg-white">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 self-start border-2 border-[#0A0A0B] bg-white px-3 py-1.5 mono text-[11px] uppercase tracking-widest mb-8 shadow-[4px_4px_0_#0A0A0B]">
              <span className="w-2 h-2 bg-[#FF4D00]" /> Pour les artisans du bâtiment
            </div>
            <h1 className="display text-[clamp(2rem,7vw,5rem)] tracking-tighter leading-[0.95]">
              Sécurisez vos devis et vos paiements avec <span className="text-[#FF4D00]">TOUS</span> vos clients.
            </h1>
            <p className="mt-8 text-lg text-zinc-700 max-w-2xl">
              Ne prenez plus aucun risque d'impayé sur <strong>vos propres chantiers</strong> : votre client verse
              l'acompte ou le budget sur le <strong>séquestre bancaire Stripe</strong> avant que vous ne posiez un outil,
              et vous êtes payé dès validation. En bonus : de <strong>nouveaux chantiers</strong> via l'annuaire,
              vos devis PDF, vos factures Factur-X 2026 et un journal de preuves opposable.
              <strong> 1 mois de Pack Pro offert à l'inscription</strong>, sans carte bancaire.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register?role=artisan&trial=1" className="btn-batisur flex items-center gap-2" data-testid="join-hero-cta">
                🎁 Réclamer mon mois offert <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/artisans" className="btn-anthracite flex items-center gap-2" data-testid="join-hero-see-directory">
                Voir l'annuaire public <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="display text-3xl text-[#FF4D00]">30j</div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Pack Pro offert</div>
              </div>
              <div>
                <div className="display text-3xl">10%</div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Commission dégressive</div>
              </div>
              <div>
                <div className="display text-3xl">24h</div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Vérification SIRET</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 flex items-center">
            <div className="w-full border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-8 shadow-[8px_8px_0_#FF4D00]">
              <Overline color="#FF4D00">// Preuves opposables</Overline>
              <div className="display text-3xl mt-2">Factur-X 2026</div>
              <p className="text-sm text-zinc-300 mt-3">
                Chaque facture générée depuis votre espace est <strong>conforme à la réforme 2026</strong> :
                PDF/A-3 avec XML EN 16931 embarqué. Prête pour vos clients pros et l'administration fiscale.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
                <div className="border border-zinc-700 p-3">
                  <Camera className="w-4 h-4 text-[#FF4D00]" />
                  <div className="font-semibold mt-2">Journal photo</div>
                  <div className="text-zinc-400">Horodatage + GPS + hash</div>
                </div>
                <div className="border border-zinc-700 p-3">
                  <BadgeCheck className="w-4 h-4 text-[#FF4D00]" />
                  <div className="font-semibold mt-2">SIRET Sirene</div>
                  <div className="text-zinc-400">Vérifié auprès de l'INSEE</div>
                </div>
                <div className="border border-zinc-700 p-3">
                  <Lock className="w-4 h-4 text-[#FF4D00]" />
                  <div className="font-semibold mt-2">Escrow Stripe</div>
                  <div className="text-zinc-400">Fonds bloqués, jamais perdus</div>
                </div>
                <div className="border border-zinc-700 p-3">
                  <Scale className="w-4 h-4 text-[#FF4D00]" />
                  <div className="font-semibold mt-2">Médiation admin</div>
                  <div className="text-zinc-400">Arbitre neutre en cas de litige</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INVITE YOUR CLIENTS — main pitch, right under hero */}
      <section className="border-b-2 border-[#0A0A0B] bg-white" data-testid="invite-clients-section">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Overline>// Votre botte secrète</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-4xl">
            Vos clients hésitent à verser l'acompte ?<br />
            <span className="text-[#FF4D00]">Offrez-leur la garantie Batisur.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-zinc-700">
            Proposez à vos clients — particuliers ou pros — de passer par Batisur Connect. Envoyez-leur votre
            lien personnel : ils publient leur chantier, vous invitent, et le budget est <strong>séquestré sur
            Stripe avant le premier coup de marteau</strong>. Le client est rassuré, il signe plus vite — et
            fini les impayés, même avec vos clients historiques.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-0 border-2 border-[#0A0A0B]">
            {[
              [Wallet, "Zéro impayé, même avec vos habitués", "Acompte ou budget total bloqué chez Stripe dès l'acceptation de votre devis. Vous démarrez le chantier en sachant que l'argent est déjà là."],
              [Sparkles, "Une image ultra professionnelle qui rassure", "Devis PDF signé Batisur, paiement séquestré, journal photo horodaté, facture Factur-X 2026 : vos clients voient un artisan structuré — et vous recommandent."],
              [Users, "Vos clients restent VOS clients", "Aucune mise en concurrence sur les missions où vous êtes invité. Vous gardez la relation, Batisur ajoute juste le filet de sécurité."],
            ].map(([Icon, t, d], i) => (
              <div key={t} className={`p-8 ${i < 2 ? "md:border-r-2 border-[#0A0A0B]" : ""} ${i < 2 ? "border-b-2 md:border-b-0 border-[#0A0A0B]" : ""}`}>
                <Icon className="w-8 h-8 text-[#FF4D00]" />
                <div className="display text-xl mt-4">{t}</div>
                <div className="text-sm text-zinc-600 mt-2">{d}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="display text-2xl">Comment ça marche ?</div>
              <div className="text-sm text-zinc-300 mt-1">
                1. Votre client s'inscrit (2 min) → 2. Il publie son chantier et <strong className="text-[#FF4D00]">vous invite nommément</strong> → 3. Vous envoyez votre devis → 4. Il paie, les fonds sont séquestrés → 5. Chantier documenté, validation, virement.
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-3" data-testid="invite-commission-note">
                Commission 10% dégressive — quand les plateformes classiques prennent ~18% sans séquestre, sans Factur-X, sans dossier de preuves.
              </div>
              <div className="mt-3 inline-flex items-center gap-2 bg-[#FF4D00] text-white px-3 py-1.5 mono text-[10px] uppercase tracking-widest" data-testid="first-invited-offer">
                <Gift className="w-3.5 h-3.5" /> Offre de lancement : 0% de commission artisan sur votre 1ère mission avec votre propre client
              </div>
            </div>
            <Link to="/register?role=artisan" className="btn-batisur shrink-0" data-testid="invite-clients-cta">
              Créer mon compte artisan <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* FULL TOOLBOX */}
      <section className="border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white" data-testid="toolbox-section">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Votre boîte à outils complète</div>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-3xl">
            Tout ce qu'il faut pour gérer un chantier, <span className="text-[#FF4D00]">au même endroit</span>.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl text-sm">
            Pas besoin d'empiler un logiciel de devis, un outil de facturation, une messagerie et un agenda :
            Batisur regroupe tout, inclus dans la commission.
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
            {[
              [FileText, "Devis en ligne + PDF pro", "Rédigez votre devis (montant, durée, acompte 30-50%) et téléchargez-le en PDF à votre nom, assurances incluses."],
              [Receipt, "Facture Factur-X 2026", "Émettez votre facture client PDF/A-3 avec XML EN 16931 embarqué — conforme à la réforme e-invoicing, TVA ou franchise 293 B."],
              [Lock, "Séquestre Stripe", "Acompte ou budget total bloqué avant le chantier, versé sur votre IBAN dès validation. Zéro impayé."],
              [Camera, "Journal de chantier", "Photos/vidéos horodatées (GPS + empreinte numérique) : avant travaux, avancement, finition. Dossier de preuve opposable."],
              [MessageSquare, "Messagerie par client", "Un fil privé avec chaque client, notifications email + push. Tous vos échanges tracés au même endroit."],
              [ClipboardList, "Organiseur kanban", "Vos chantiers classés : à chiffrer, devis envoyés, en cours, à valider, terminés — avec alertes de retard."],
              [CalendarDays, "Agenda des échéances", "Toutes vos dates limites sur un calendrier mensuel pour planifier la semaine d'un coup d'œil."],
              [Link2, "Lien d'invitation client", "Votre lien personnel à envoyer : le client publie son chantier et vous invite automatiquement, sans concurrence."],
              [BadgeCheck, "Badges de confiance", "SIRET vérifié INSEE, assurance décennale et RC Pro affichées sur votre profil public et vos devis."],
              [Award, "Certificat de bonne fin", "Un certificat PDF signé Batisur à chaque chantier validé — votre portfolio de sérieux s'agrandit tout seul."],
              [BellRing, "Relances automatiques", "Devis sans réponse, solde en attente, avis client : Batisur relance à votre place, au bon moment."],
              [Scale, "Médiation neutre", "En cas de litige, un médiateur tranche sur la base du journal de preuves. Les fonds restent séquestrés."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="bg-[#0A0A0B] p-6 hover:bg-zinc-900 transition-colors">
                <Icon className="w-6 h-6 text-[#FF4D00]" />
                <div className="font-bold mt-3 text-sm">{t}</div>
                <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOMO — Prospects awaiting artisans */}
      <WaitingProspectsWidget variant="pro-page" limit={6} />

      {/* WHY */}
      <section className="border-b-2 border-[#0A0A0B] bg-[#F7F7F9]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Overline>// Pourquoi rejoindre Batisur</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-3xl">
            L'artisanat mérite mieux que la course aux devis gratuits.
          </h2>
          <p className="mt-4 text-lg text-zinc-700 max-w-3xl">
            Nous ne vendons pas des <em>leads</em>. Nous mettons en scène votre expertise face à des clients qui ont <strong>déjà provisionné</strong> le budget.
          </p>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[#0A0A0B] bg-white">
            {[
              [Lock, "Paiement garanti", "L'argent est bloqué en escrow Stripe avant démarrage. Vous ne travaillez plus jamais 'à l'espoir'."],
              [TrendingUp, "Chantiers qualifiés", "Chaque demandeur est identifié : particuliers, architectes, promoteurs, syndics, bailleurs sociaux."],
              [Camera, "Preuves valorisées", "Le journal horodaté prouve la qualité du travail — mieux qu'un avis Google, plus dur à contester."],
              [Award, "Réputation qui rapporte", "Notes vérifiées, badge Or ou Pro, mise en avant géo-ciblée. Votre historique devient un actif commercial."],
            ].map(([Icon, t, d], i) => (
              <div key={t} className={`p-6 ${i < 3 ? "border-r-2 border-b-2 md:border-b-2 lg:border-b-0" : "border-b-2 lg:border-b-0"} border-[#0A0A0B]`}>
                <Icon className="w-6 h-6 text-[#FF4D00]" />
                <div className="display text-xl mt-4">{t}</div>
                <div className="text-sm text-zinc-600 mt-2">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b-2 border-[#0A0A0B] bg-white" id="fonctionnement">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Overline>// Le parcours d'un chantier Batisur</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-3xl">De la demande au virement, sans stress.</h2>

          <div className="mt-12 grid lg:grid-cols-5 gap-0 border-2 border-[#0A0A0B]">
            {[
              ["01", "Inscription", "5 minutes. SIRET vérifié auto. Profil public dès validation."],
              ["02", "Devis", "Un demandeur publie sa mission. Vous proposez votre prix ferme."],
              ["03", "Séquestre", "À l'acceptation, le client verse la totalité — bloquée par Stripe."],
              ["04", "Chantier", "Vous documentez via le journal photo/vidéo horodaté."],
              ["05", "Virement", "Validation client → paiement libéré → facture Factur-X générée."],
            ].map(([n, t, d], i) => (
              <div key={n} className={`p-6 ${i < 4 ? "border-r-2 border-[#0A0A0B]" : ""}`}>
                <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">Étape {n}</div>
                <div className="display text-xl mt-3">{t}</div>
                <div className="text-sm text-zinc-600 mt-2">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Overline color="#FF4D00">// Batisur vs ailleurs</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-3xl">
            Assez de payer pour des <span className="text-[#FF4D00]">leads froids</span>.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-0 border-2 border-white">
            <div className="p-8 border-r-2 border-white">
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-4">Plateformes classiques</div>
              {[
                "Vous payez le lead avant même de parler au client",
                "Concurrence de 5 à 10 artisans sur chaque demande",
                "Aucune garantie de paiement — vous portez le risque",
                "Aucune preuve opposable en cas de litige",
                "Facturation à faire vous-même, non conforme 2026",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3 py-2 text-sm">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  <span className="text-zinc-300">{t}</span>
                </div>
              ))}
            </div>
            <div className="p-8 bg-[#FF4D00]">
              <div className="mono text-[11px] uppercase tracking-widest text-white/80 mb-4">Batisur Connect</div>
              {[
                "0€ tant que vous ne signez pas de mission",
                "Devis directs — pas de coût par lead",
                "Fonds séquestrés Stripe avant début du chantier",
                "Journal horodaté + Factur-X = dossier béton",
                "Facturation générée automatiquement, conforme 2026",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3 py-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-white mt-0.5 shrink-0" strokeWidth={3} />
                  <span className="text-white">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="border-b-2 border-[#0A0A0B] bg-[#F7F7F9]" id="tarifs">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Overline>// Trois niveaux, à votre rythme</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3">Commencez gratuitement. Montez en visibilité quand vous le voulez.</h2>

          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            {/* Standard */}
            <div className="border-2 border-[#0A0A0B] bg-white p-5 sm:p-7 flex flex-col">
              <Overline color="#0A0A0B">Standard</Overline>
              <div className="display text-2xl sm:text-3xl mt-2">Gratuit</div>
              <div className="display text-4xl sm:text-5xl mt-4">0€</div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">à vie</div>
              <ul className="mt-6 space-y-2 text-sm flex-1">
                {["Profil public annuaire", "Devis illimités", "Paiement séquestré Stripe", "Journal photo/vidéo", "Messagerie intégrée"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <Link to="/register?role=artisan" className="btn-anthracite mt-6 text-center" data-testid="tier-standard-cta">Créer mon profil</Link>
            </div>

            {/* Pro */}
            <div className="border-2 border-[#FF4D00] bg-[#0A0A0B] text-white p-5 sm:p-7 flex flex-col relative">
              <div className="absolute -top-3 left-4 sm:left-7 bg-[#FF4D00] px-3 py-1 mono text-[10px] uppercase tracking-widest font-semibold">Populaire · 1er mois offert 🎁</div>
              <Overline color="#FF4D00">Batisur Pro</Overline>
              <div className="display text-2xl sm:text-3xl mt-2">Pack Pro</div>
              <div className="flex items-baseline gap-2 mt-3">
                <div className="display text-4xl sm:text-6xl">29€</div>
                <div className="mono text-sm text-zinc-400">/ 30 jours</div>
              </div>
              <div className="mt-2 mono text-[10px] uppercase tracking-widest text-[#FF4D00]">
                Le 1er mois est offert · Sans carte à l'inscription
              </div>
              <ul className="mt-6 space-y-2 text-sm flex-1">
                {["Tout du plan Standard", "Mise en avant prioritaire annuaire", "Badge Premium vérifié", "Notification instantanée nouveaux chantiers", "Support prioritaire (4h ouvrées)"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#FF4D00] mt-0.5 shrink-0" strokeWidth={2.5} />{f}</li>
                ))}
              </ul>
              <Link to="/register?role=artisan&trial=1" className="btn-batisur mt-6 text-center" data-testid="tier-pro-cta">Démarrer mon mois offert 🎁</Link>
            </div>

            {/* Or */}
            <div className="border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white p-5 sm:p-7 flex flex-col relative">
              <div className="absolute -top-3 left-4 sm:left-7 bg-amber-500 text-[#0A0A0B] px-3 py-1 mono text-[10px] uppercase tracking-widest font-semibold">Élite</div>
              <Overline color="#b45309">Artisan Or</Overline>
              <div className="display text-2xl sm:text-3xl mt-2">Pack Confiance</div>
              <div className="flex items-baseline gap-2 mt-3">
                <div className="display text-4xl sm:text-6xl text-amber-700">49€</div>
                <div className="mono text-sm text-zinc-500">/ 30 jours</div>
              </div>
              <ul className="mt-6 space-y-2 text-sm flex-1">
                {["Tout du plan Pro", "Badge Or exclusif", "Tête d'annuaire absolue", "Mise en avant landing page", "Conseiller Batisur dédié"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Award className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <Link to="/register?role=artisan" className="mt-6 text-center px-5 py-3 bg-amber-500 text-[#0A0A0B] font-semibold mono text-xs uppercase tracking-widest hover:bg-amber-400" data-testid="tier-or-cta">Passer Or</Link>
            </div>
          </div>

          <div className="mt-8 border-2 border-[#0A0A0B] bg-white p-6 flex items-start gap-4">
            <Wallet className="w-6 h-6 text-[#FF4D00] shrink-0 mt-1" />
            <div>
              <div className="display text-lg">Commission plateforme : 10% dégressive</div>
              <p className="text-sm text-zinc-600 mt-1">
                Sur chaque mission validée, Batisur prélève 10% côté demandeur + 10% côté artisan.
                À partir de 15 000€ HT, la part artisan tombe à 7% puis 5% au-delà de 50 000€ HT.
                <strong> Aucune commission sur les devis refusés.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO — Long-tail queries */}
      <section className="border-b-2 border-[#0A0A0B] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <Overline>// Vous êtes artisan et…</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-4xl">Trouvez le chantier que vous méritez, sur toute la France.</h2>
          <p className="mt-4 text-lg text-zinc-700 max-w-3xl">
            Que vous soyez <strong>plombier, électricien, maçon, peintre, carreleur, menuisier, chauffagiste, couvreur, charpentier, façadier, plaquiste, poseur de cuisine, artisan salle de bain, spécialiste rénovation énergétique, entreprise du bâtiment</strong> ou auto-entrepreneur BTP — Batisur Connect est fait pour vous.
          </p>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              {
                q: "Vous cherchez un chantier proche de chez vous ?",
                icon: MapPin,
                body: "Notre annuaire géolocalisé remonte les demandes de travaux BTP à moins de 30 km de votre secteur. Rénovation d'appartement, extension de maison, ravalement de façade, réfection de toiture, création de salle de bain… vous recevez uniquement les missions qui correspondent à votre spécialité et votre zone d'intervention. Fini les leads froids Paris pour un artisan de Lyon.",
              },
              {
                q: "Vous voulez être payé rapidement et sans relance ?",
                icon: Zap,
                body: "Le paiement client est <strong>séquestré via Stripe avant le démarrage</strong> du chantier. Dès validation par le demandeur (ou 7 jours après la fin déclarée sans réserve), les fonds sont libérés vers votre IBAN sous 2 à 5 jours ouvrés. Zéro chèque en attente, zéro impayé, zéro procédure de recouvrement. C'est la fin du casse-tête trésorerie pour les artisans du bâtiment.",
              },
              {
                q: "Vous voulez arrêter la course aux devis gratuits ?",
                icon: Star,
                body: "Sur Batisur, vous ne payez <strong>aucun lead à l'avance</strong>. Vous ne payez que si vous signez. Notre modèle : commission dégressive de 10% (puis 7% et 5% sur les gros chantiers) prélevée uniquement à la validation. Contrairement aux plateformes classiques qui vendent le même contact à 10 artisans concurrents, votre devis Batisur va au demandeur directement.",
              },
              {
                q: "Vous voulez sécuriser votre trésorerie et votre décennale ?",
                icon: ShieldCheck,
                body: "Chaque chantier Batisur génère un <strong>dossier de preuves opposables</strong> : photos et vidéos horodatées avec géolocalisation GPS, journal quotidien signé, échanges messagerie tracés, facture Factur-X 2026 conforme. Ce dossier protège votre garantie décennale et sert de pièce en cas de litige avec l'assurance ou devant le tribunal.",
              },
              {
                q: "Vous voulez développer votre activité sans commercial ?",
                icon: TrendingUp,
                body: "Batisur remplace votre force commerciale : les demandeurs viennent à vous. Particuliers en rénovation, architectes en maîtrise d'œuvre, promoteurs immobiliers, syndics de copropriété, bailleurs sociaux et investisseurs locatifs publient leurs projets. Vous répondez au devis. Plus besoin de porte-à-porte, de flyers, de Google Ads coûteux ou de publicité Facebook.",
              },
              {
                q: "Vous voulez une facture conforme à la réforme 2026 ?",
                icon: FileText,
                body: "La facture électronique B2B devient obligatoire en France. Batisur génère automatiquement vos factures au <strong>format Factur-X (PDF/A-3 + XML EN 16931)</strong>, conforme à la réforme 2026. Vos clients pros (architectes, promoteurs, entreprises BTP, collectivités) peuvent les intégrer directement dans leur ERP. Économie estimée : 500 à 1200€/an de logiciel de facturation.",
              },
            ].map(({ q, icon: Icon, body }, i) => (
              <article key={q} className="border-2 border-[#0A0A0B] bg-[#F7F7F9] p-6 hover:bg-white transition-colors" data-testid={`artisan-seo-block-${i}`}>
                <Icon className="w-6 h-6 text-[#FF4D00]" />
                <h3 className="display text-2xl mt-3 leading-tight">{q}</h3>
                <p className="mt-4 text-sm text-zinc-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
              </article>
            ))}
          </div>

          <div className="mt-12 border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-8">
            <Overline color="#FF4D00">// Mots-clés Batisur pour artisans</Overline>
            <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
              Trouver des chantiers de rénovation · Trouver des clients pour artisan · Plateforme de mise en relation BTP · Paiement sécurisé travaux · Escrow chantier · Facturation Factur-X artisan · Auto-entrepreneur bâtiment sans commercial · Marketplace artisans du bâtiment France · Trouver des missions pour plombier électricien maçon · Éviter les impayés BTP · Trésorerie artisan sécurisée · Preuves de fin de chantier · Journal de chantier numérique · Annuaire artisans vérifiés SIRET.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b-2 border-[#0A0A0B] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <Overline>// Questions fréquentes artisans</Overline>
          <h2 className="display text-4xl mt-3">Ce que les artisans nous demandent le plus.</h2>

          <div className="mt-10 space-y-4">
            {[
              ["Puis-je utiliser Batisur Connect avec mes propres clients ?", "Oui, c'est même fortement conseillé ! Si vous avez déjà un client mais qu'il hésite à verser l'acompte, invitez-le via votre lien personnel (disponible sur votre tableau de bord). Le budget est consigné sur un compte séquestre Stripe neutre : votre client est rassuré, il signe plus vite, et votre trésorerie est garantie à 100% — acompte comme solde. Bonus : votre 1ère mission avec votre propre client est à 0% de commission artisan."],
              ["Quand suis-je payé ?", "Dès validation du chantier par le demandeur (ou automatiquement 7 jours après la fin déclarée, sauf litige). Virement Stripe sous 2 à 5 jours ouvrés vers votre IBAN."],
              ["Dois-je obligatoirement souscrire un pack ?", "Non. Le plan Standard est gratuit à vie. Les packs Pro et Or servent uniquement à améliorer votre visibilité dans l'annuaire."],
              ["Comment se déroule un litige ?", "Le journal photo/vidéo horodaté et les échanges messagerie servent de dossier. L'admin Batisur tranche en médiation neutre. Les fonds séquestrés ne sont libérés qu'après décision."],
              ["Mes factures sont-elles conformes 2026 ?", "Oui. Chaque facture générée sur Batisur est un PDF/A-3 avec XML Factur-X EN 16931 embarqué. Prête pour la réforme de l'e-invoicing B2B 2026."],
              ["Puis-je refuser un chantier ?", "Absolument. Vous êtes libre d'accepter ou décliner chaque demande. Aucune obligation, aucune pénalité."],
              ["Batisur remplace-t-il ma comptabilité ?", "Non, mais nous générons vos factures et exportons les données TVA. Votre expert-comptable récupère tout au format PDF/A-3 + CSV."],
            ].map(([q, a], i) => (
              <details key={q} className="border-2 border-[#0A0A0B] bg-white group" data-testid={`join-faq-${i}`}>
                <summary className="cursor-pointer list-none p-5 flex justify-between items-center hover:bg-zinc-50">
                  <div className="font-semibold text-[#0A0A0B]">{q}</div>
                  <div className="mono text-xs text-[#FF4D00] group-open:rotate-45 transition-transform">＋</div>
                </summary>
                <div className="p-5 border-t-2 border-[#0A0A0B] text-sm text-zinc-700">{a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* MEDIATEUR À VOS CÔTÉS */}
      <section className="border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white overflow-hidden relative" data-testid="mediator-section">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <Overline color="#FF4D00">// Vous n'êtes jamais seul</Overline>
              <h2 className="display text-4xl lg:text-5xl mt-3 leading-[0.95]">
                Un <span className="text-[#FF4D00]">médiateur</span><br />à vos côtés<br />sur chaque chantier.
              </h2>
              <p className="mt-6 text-lg text-zinc-300 leading-relaxed">
                Dès qu'un demandeur <strong className="text-white">signe votre devis</strong> et que les
                <strong className="text-white"> fonds sont séquestrés sur Stripe</strong>, un médiateur Batisur Connect vous accompagne :
                depuis le paiement jusqu'à la libération de vos fonds.
              </p>
              <p className="mt-4 text-sm text-zinc-400">
                C'est votre point de contact unique côté plateforme. Il connaît votre dossier, votre journal
                de chantier, et il défend un traitement équitable en cas de désaccord. Vous n'êtes plus jamais seul face au client.
              </p>
            </div>

            <div className="lg:col-span-7 grid md:grid-cols-2 gap-4">
              {[
                {
                  icon: Lock,
                  title: "Étape 1 · Devis signé, budget séquestré",
                  body: "Vous êtes notifié dès l'acceptation. Le budget est déjà sur Stripe. Votre médiateur ouvre le dossier de chantier et vous confirme le compte séquestre.",
                },
                {
                  icon: Camera,
                  title: "Étape 2 · Chantier documenté",
                  body: "Vous alimentez le journal photo/vidéo. Chaque preuve est horodatée avec GPS et watermark. Votre médiateur voit tout et peut relancer le demandeur si besoin.",
                },
                {
                  icon: ShieldCheck,
                  title: "Étape 3 · Photo de Finition",
                  body: "Vous publiez la photo de Finition. Le compte à rebours de validation démarre côté demandeur. Le médiateur s'assure qu'il valide sans traîner.",
                },
                {
                  icon: Wallet,
                  title: "Étape 4 · Fonds libérés vers votre IBAN",
                  body: "Validation reçue → votre médiateur déclenche la libération Stripe. Virement sous 2 à 5 jours ouvrés. Facture Factur-X 2026 générée automatiquement.",
                },
              ].map(({ icon: Icon, title, body }, i) => (
                <div key={title} className="border-2 border-white/20 bg-white/5 p-5 hover:bg-white/10 transition-colors backdrop-blur" data-testid={`artisan-mediator-step-${i}`}>
                  <Icon className="w-7 h-7 text-[#FF4D00]" />
                  <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] mt-3">{title}</div>
                  <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 border-2 border-[#FF4D00] p-6 bg-[#FF4D00]/10">
            <div className="flex items-start gap-4">
              <Scale className="w-8 h-8 text-[#FF4D00] shrink-0" />
              <div>
                <div className="display text-xl">En cas de désaccord ? Médiation amiable gratuite en 30 jours.</div>
                <p className="text-sm text-zinc-300 mt-2">
                  Si le demandeur refuse de valider malgré votre photo de Finition, votre médiateur ouvre un dossier de médiation. Il analyse objectivement le journal photo, les échanges, le devis, et propose un accord neutre. Ce service est <strong className="text-white">100% gratuit et conforme à nos CGU (article 6.2)</strong>. Votre travail exécuté est défendu — pas balayé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <HardHat className="w-12 h-12 mx-auto" />
          <h2 className="display text-4xl lg:text-5xl mt-4">Prêt à ne plus courir après vos paiements ?</h2>
          <p className="mt-4 text-lg opacity-90">Inscription gratuite. Aucune carte demandée. Votre premier chantier peut être signé cette semaine.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register?role=artisan" className="bg-white text-[#FF4D00] font-semibold px-6 py-3 mono text-xs uppercase tracking-widest hover:bg-zinc-100" data-testid="join-cta-register">
              Créer mon compte artisan →
            </Link>
            <Link to="/artisans" className="border-2 border-white text-white font-semibold px-6 py-3 mono text-xs uppercase tracking-widest hover:bg-white hover:text-[#FF4D00]" data-testid="join-cta-directory">
              Voir des profils artisans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
